import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TEST_IDS } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { Loader2, ArrowRight, Sparkles, ShieldCheck, Globe2, FileCheck2, Zap, MailCheck } from "lucide-react";

// Live "fake transcript" lines that scroll in the brand panel preview.
// They're decorative only — never reflect a real meeting.
const TICKER_LINES = [
  { who: "Priya", what: "Let's lock the launch date as March 14." },
  { who: "Karan", what: "Action: send the SOC2 evidence by Friday." },
  { who: "Maya",  what: "Decision: ship the new pricing on March 1." },
  { who: "Dev",   what: "Risk — the API rate-limit hits at 10k RPS." },
  { who: "Priya", what: "Owner: Karan. Due: next standup." },
  { who: "Maya",  what: "On agenda — Q1 metrics review next." },
];

const STATS = [
  { v: "98.4%", l: "transcription accuracy" },
  { v: "12", l: "Indian languages" },
  { v: "<15s", l: "live caption latency" },
];

const TRUST = [
  { icon: ShieldCheck, label: "SOC2-ready security" },
  { icon: FileCheck2, label: "GST-compliant billing" },
  { icon: Globe2, label: "Hindi · Tamil · Telugu · English" },
  { icon: Zap, label: "99.9% uptime" },
];

const SIZES = ["1–10", "11–50", "51–200", "201–1000", "1000+"];

export default function Login() {
  const { user, login, registerStart } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [size, setSize] = useState(SIZES[1]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/", { replace: true });
      } else {
        await registerStart({ name, email, org_name: orgName, company_size: size });
        setSent(true);
      }
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  // Duplicate the lines so the marquee can seamlessly loop on -50%.
  const tickerLoop = [...TICKER_LINES, ...TICKER_LINES];

  return (
    <div className="min-h-screen grid md:grid-cols-[1.1fr_1fr] bg-background overflow-hidden">
      {/* ──────────────────────────── LEFT BRAND PANEL ──────────────────────────── */}
      <div
        className="relative hidden md:block"
        style={{ background: "linear-gradient(180deg, hsl(var(--tone-lavender)) 0%, hsl(var(--background)) 100%)" }}
      >
        <div className="yl-orb yl-orb-a" />
        <div className="yl-orb yl-orb-b" />
        <div className="yl-orb yl-orb-c" />
        <div className="yl-grain" />

        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-foreground">
          {/* Top — logo */}
          <div className="flex items-center gap-3 yl-fade-up" style={{ animationDelay: "60ms" }}>
            <div className="w-11 h-11 rounded-xl grid place-items-center shadow-lift" style={{ background: "hsl(var(--navy))" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2 L20 15 H13 Z" fill="hsl(var(--cyan))" />
                <path d="M12 2 L4 15 H11 Z" fill="#ffffff" fillOpacity="0.92" />
                <path d="M9 17 H15 L13.5 22 H10.5 Z" fill="hsl(var(--gold))" />
              </svg>
            </div>
            <div>
              <div className="font-heading text-2xl leading-none">
                Meet<span style={{ color: "hsl(var(--cyan))" }}>IQ</span><span style={{ color: "hsl(var(--gold))" }}>360</span>
              </div>
              <div className="eyebrow mt-1.5">by Yanthra Labs</div>
            </div>
          </div>

          {/* Middle — headline + glass preview */}
          <div className="space-y-8">
            <div className="yl-fade-up" style={{ animationDelay: "160ms" }}>
              <div className="inline-flex items-center gap-2 rounded-full yl-glass px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-primary-hover">
                <Sparkles className="w-3 h-3" strokeWidth={2.4} />
                Yanthra AI Agent · multilingual engine
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-6">
                Turn conversations
                <br />
                into <span className="yl-shimmer-text not-italic">decision intelligence.</span>
              </h1>
              <p className="mt-5 text-[15px] text-muted-foreground max-w-md leading-relaxed">
                Record and transcribe in 12 Indian languages, auto-extract decisions,
                owners and due dates, and keep GST-compliant regional billing — all in one workspace.
              </p>
            </div>

            {/* Glass card — fake "live meeting" preview */}
            <div
              data-testid="login-live-preview"
              className="yl-glass yl-fade-up shadow-lift rounded-3xl p-5 max-w-md"
              style={{ animationDelay: "320ms" }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="relative inline-flex w-2.5 h-2.5">
                    <span className="absolute inset-0 rounded-full bg-rose-500 yl-pulse-dot" />
                    <span className="absolute inset-0 rounded-full bg-rose-500" />
                  </span>
                  <span className="eyebrow text-rose-700">Live · Product Sync</span>
                </div>
                <div className="flex items-end gap-[3px] h-4">
                  {[0, 0.15, 0.3, 0.45, 0.6, 0.75].map((d, i) => (
                    <span
                      key={i}
                      className="yl-wave-bar block w-[3px] bg-primary rounded-full"
                      style={{ height: "100%", animationDelay: `${d}s` }}
                    />
                  ))}
                </div>
              </div>

              <div className="relative h-32 overflow-hidden mask-fade">
                <div className="yl-ticker space-y-2.5">
                  {tickerLoop.map((line, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-secondary text-primary-hover grid place-items-center text-[11px] font-bold">
                        {line.who[0]}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground/60">
                          {line.who}
                        </div>
                        <div className="text-foreground/90 leading-snug">{line.what}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="absolute inset-x-0 bottom-0 h-12 pointer-events-none"
                  style={{ background: "linear-gradient(to bottom, transparent, hsl(var(--glass-bg) / .55))" }}
                />
              </div>

              <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-3 gap-3">
                {[
                  { v: "3", l: "decisions" },
                  { v: "5", l: "action items" },
                  { v: "92%", l: "on agenda" },
                ].map((kpi) => (
                  <div key={kpi.l}>
                    <div className="font-heading text-2xl leading-none">{kpi.v}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] mt-1 text-muted-foreground">
                      {kpi.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-2.5 max-w-md yl-fade-up" style={{ animationDelay: "420ms" }}>
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-2 rounded-xl yl-glass px-3 py-2">
                  <t.icon className="w-4 h-4 shrink-0" style={{ color: "hsl(var(--cyan))" }} strokeWidth={2} />
                  <span className="text-[11px] font-semibold text-foreground/75 leading-tight">{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom — stat row */}
          <div className="flex items-center justify-between gap-6 yl-fade-up" style={{ animationDelay: "520ms" }}>
            <div className="flex items-center gap-8">
              {STATS.map((s) => (
                <div key={s.l}>
                  <div className="font-heading text-2xl leading-none">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] mt-1 text-muted-foreground">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
            <div className="eyebrow opacity-70 hidden lg:block">Enterprise meeting intelligence</div>
          </div>
        </div>
      </div>

      {/* ──────────────────────────── RIGHT — AUTH ──────────────────────────── */}
      <div className="relative flex items-center justify-center p-8">
        <div className="absolute inset-0 md:hidden">
          <div className="yl-orb yl-orb-a opacity-50" />
          <div className="yl-orb yl-orb-b opacity-50" />
        </div>

        <div className="relative w-full max-w-sm yl-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="md:hidden mb-8 text-center">
            <div className="font-heading text-3xl">Meet<span style={{ color: "hsl(var(--cyan))" }}>IQ</span><span style={{ color: "hsl(var(--gold))" }}>360</span></div>
          </div>

          {/* Toggle */}
          <div className="inline-flex w-full rounded-full border border-border p-1 bg-secondary/40 mb-6">
            <button
              type="button"
              data-testid={TEST_IDS.loginTab}
              onClick={() => { setMode("login"); setErr(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "login" ? "text-white shadow-sm" : "text-foreground/60"}`}
              style={mode === "login" ? { background: "hsl(var(--navy))" } : {}}
            >
              Sign in
            </button>
            <button
              type="button"
              data-testid={TEST_IDS.registerTab}
              onClick={() => { setMode("register"); setErr(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "register" ? "text-white shadow-sm" : "text-foreground/60"}`}
              style={mode === "register" ? { background: "hsl(var(--navy))" } : {}}
            >
              Create organisation
            </button>
          </div>

          <h2 className="font-heading text-3xl sm:text-4xl leading-[1.06]">
            {sent ? "Check your inbox." : mode === "login" ? "Welcome back." : "Start your workspace."}
          </h2>
          <p className="text-sm text-muted-foreground mt-2.5">
            {sent
              ? <>We sent a secure verification link to <b className="text-foreground">{email}</b>. Open it to confirm your email, then set your password. The link expires in 30 minutes.</>
              : mode === "login"
              ? "Sign in with your work email to access your meetings, decisions and tasks."
              : "First, verify your work email. We'll email you a secure link — then you'll set a password."}
          </p>

          {sent && (
            <div data-testid="register-sent" className="mt-7 rounded-3xl border border-border bg-secondary/40 p-6 text-center">
              <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto" style={{ background: "hsl(var(--navy))" }}>
                <MailCheck className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm text-muted-foreground mt-4">Didn&apos;t get it? Check spam, or
                <button type="button" onClick={() => { setSent(false); }} className="ml-1 text-primary font-semibold hover:underline" data-testid="register-use-different">use a different email</button>.
              </p>
              <button type="button" onClick={() => { setMode("login"); setSent(false); setErr(""); }} className="mt-5 inline-flex items-center justify-center rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="register-back-to-login">
                Back to sign in
              </button>
            </div>
          )}

          <form data-testid="login-form" onSubmit={submit} className={`mt-7 space-y-4 ${sent ? "hidden" : ""}`}>
            {mode === "register" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="eyebrow">Full name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                    data-testid={TEST_IDS.registerNameInput} placeholder="Priya Sharma" required
                    className="rounded-2xl h-12 px-4 bg-card border-border focus:border-primary" autoFocus />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org" className="eyebrow">Organisation name</Label>
                  <Input id="org" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                    data-testid={TEST_IDS.registerOrgInput} placeholder="Acme Pvt Ltd" required
                    className="rounded-2xl h-12 px-4 bg-card border-border focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="size" className="eyebrow">Team size</Label>
                  <select id="size" value={size} onChange={(e) => setSize(e.target.value)}
                    data-testid={TEST_IDS.registerSizeSelect}
                    className="w-full rounded-2xl h-12 px-4 bg-card border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring text-sm">
                    {SIZES.map((s) => <option key={s} value={s}>{s} people</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="eyebrow">Work email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={TEST_IDS.loginEmailInput} placeholder="you@company.com" required
                autoFocus={mode === "login"}
                className="rounded-2xl h-12 px-4 bg-card border-border focus:border-primary" />
            </div>
            {mode === "login" && (
              <div className="space-y-2">
                <Label htmlFor="password" className="eyebrow">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid={TEST_IDS.loginPasswordInput} placeholder="••••••••••" required
                  className="rounded-2xl h-12 px-4 bg-card border-border focus:border-primary" />
              </div>
            )}

            {err && (
              <div data-testid={TEST_IDS.loginErrorMsg} className="tone-blush rounded-2xl px-4 py-3 text-sm border border-rose-200/60">
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid={mode === "login" ? TEST_IDS.loginSubmitBtn : TEST_IDS.registerSubmitBtn}
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] shadow-lift transition-transform active:scale-[0.98] group"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-2 font-semibold tracking-wide">
                  {mode === "login" ? "Sign in" : "Send verification link"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {mode === "login" ? (
                <>New to MeetIQ360?{" "}
                  <button type="button" onClick={() => { setMode("register"); setErr(""); }} className="text-primary font-semibold hover:text-[hsl(var(--primary-hover))] underline-offset-4 hover:underline" data-testid="switch-to-register">
                    Create an organisation
                  </button>{" "}— it&apos;s free to start.</>
              ) : (
                <>Already have an account?{" "}
                  <button type="button" onClick={() => { setMode("login"); setErr(""); }} className="text-primary font-semibold hover:text-[hsl(var(--primary-hover))] underline-offset-4 hover:underline" data-testid="switch-to-login">
                    Sign in
                  </button>.</>
              )}
            </p>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 text-center eyebrow opacity-60">
          © Yanthra Labs · MeetIQ360
        </div>
      </div>
    </div>
  );
}
